/**
 * Who is signed in, and what they may do.
 *
 * The permission list comes from the server at sign-in and is the same set of
 * ROUTE codes the API checks. So the sidebar cannot offer a screen the API
 * would refuse, and hiding a button is never the only thing protecting an
 * action.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authApi } from '../services/endpoints';
import { tokenStore } from '../services/api';

export const login = createAsyncThunk('auth/login', async ({ userName, password }) => {
    const session = await authApi.login(userName, password);
    tokenStore.set(session);
    return session;
});

export const loginWithPin = createAsyncThunk('auth/loginWithPin', async ({ pin }) => {
    const session = await authApi.loginWithPin(pin);
    tokenStore.set(session);
    return session;
});

export const logout = createAsyncThunk('auth/logout', async () => {
    // Best effort: the local session is cleared whether or not the server is
    // reachable. A cashier at the end of a shift should not be stuck signed in
    // because the API is briefly down.
    try {
        await authApi.logout(tokenStore.getSessionId());
    } catch {
        /* ignore */
    }
    tokenStore.clear();
});

/** Restore a session on reload from the token in local storage. */
export const restoreSession = createAsyncThunk('auth/restore', async (_, { rejectWithValue }) => {
    if (!tokenStore.get()) return rejectWithValue('no-token');
    try {
        return await authApi.profile();
    } catch (err) {
        tokenStore.clear();
        return rejectWithValue(err.message);
    }
});

const initialState = {
    user: null,
    permissions: [],
    roles: [],
    status: 'idle',        // idle | loading | authenticated | error
    error: null,
    // True until restoreSession settles, so the router does not flash the login
    // screen at someone who is already signed in.
    isRestoring: true
};

const applySession = (state, session) => {
    state.user = session.user;
    state.roles = session.user?.roles || [];
    state.permissions = session.permissions || [];
    state.status = 'authenticated';
    state.error = null;
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        // Called by the api layer when a request comes back 401 and cannot be
        // refreshed - the session is gone, whatever this slice thought.
        sessionExpired: (state) => {
            state.user = null;
            state.permissions = [];
            state.roles = [];
            state.status = 'idle';
            state.error = 'Your session expired. Sign in again.';
        },
        clearError: (state) => { state.error = null; }
    },
    extraReducers: (builder) => {
        builder
            .addCase(login.pending, (state) => { state.status = 'loading'; state.error = null; })
            .addCase(login.fulfilled, (state, action) => applySession(state, action.payload))
            .addCase(login.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message;
            })

            .addCase(loginWithPin.pending, (state) => { state.status = 'loading'; state.error = null; })
            .addCase(loginWithPin.fulfilled, (state, action) => applySession(state, action.payload))
            .addCase(loginWithPin.rejected, (state, action) => {
                state.status = 'error';
                state.error = action.error.message;
            })

            .addCase(logout.fulfilled, (state) => {
                state.user = null;
                state.permissions = [];
                state.roles = [];
                state.status = 'idle';
            })

            .addCase(restoreSession.pending, (state) => { state.isRestoring = true; })
            .addCase(restoreSession.fulfilled, (state, action) => {
                state.user = {
                    userId: action.payload.userId,
                    userName: action.payload.userName,
                    displayName: action.payload.displayName,
                    designation: action.payload.designation,
                    photoPath: action.payload.photoPath,
                    roles: action.payload.roles,
                    mustChangePassword: action.payload.mustChangePassword
                };
                state.roles = action.payload.roles || [];
                state.permissions = action.payload.permissions || [];
                state.status = 'authenticated';
                state.isRestoring = false;
            })
            .addCase(restoreSession.rejected, (state) => {
                state.status = 'idle';
                state.isRestoring = false;
            });
    }
});

export const { sessionExpired, clearError } = authSlice.actions;

export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.status === 'authenticated';

/**
 * Permission check. An Owner passes everything - there is always one account
 * that can fix a misconfigured role.
 */
export const selectCan = (state) => (code) => {
    if (state.auth.roles?.includes('Owner')) return true;
    return state.auth.permissions?.some((p) => p.code === code && p.canView);
};

/**
 * The supplier's support account.
 *
 * Kept separate from selectCan because that one passes an Owner on every code,
 * which is right for the shop's own screens and wrong for the one that decides
 * what those screens are.
 */
export const selectIsSuperAdmin = (state) =>
    !!state.auth.roles?.includes('Super Admin');

/** The sidebar: the SCREEN permissions this user holds, in order. */
export const selectMenuItems = (state) =>
    (state.auth.permissions || [])
        .filter((p) => p.kind === 'SCREEN' && p.canView && p.path)
        .sort((a, b) => a.sequence - b.sequence);

export default authSlice.reducer;
